import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import {
  ArrowRight, Building2, Code, GraduationCap, Users, Trophy, Brain,
  Search, ClipboardList, Target, Shield, Briefcase, FileText, Award,
  TrendingUp, LineChart, Globe, Sparkles, X, AlertCircle, Calendar, MapPin,
  ExternalLink, ChevronLeft, ChevronRight, Menu, Quote, Star
} from 'lucide-react';
import axios from 'axios';
import Logo from '../components/landing/Logo';
import GrandFooter from '../components/landing/GrandFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ─── Animated Section Wrapper ─── */
const FadeInSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ─── Animated Counter ─── */
const AnimatedCounter = ({ end, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;
    let current = 0;
    const duration = 2000;
    const steps = 60;
    const increment = end / steps;
    const timer = setInterval(() => {
      current += increment;
      if (current >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isInView, end]);

  return (
    <span ref={ref}>
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
};

/* ─── Nav Bar ─── */
const TopNav = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      <header 
        className={`sticky top-0 inset-x-0 transition-all duration-300 border-b ${mobileOpen ? 'z-[60]' : 'z-40'} ${scrolled ? 'bg-white/85 backdrop-blur-md border-[#E5E5E5] py-3' : 'bg-white/70 backdrop-blur-sm border-transparent py-5'}`}
        data-testid="top-nav"
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Logo size="md" />
            <nav className="hidden md:flex items-center gap-6 text-[13px] font-semibold tracking-wide text-[#525252]">
              <Link to="/" className="hover:text-[#B91C1C] transition-colors" data-testid="nav-link-home">Home</Link>
              <Link to="/codearena" className="hover:text-[#B91C1C] transition-colors" data-testid="nav-link-arena">EvalentX Arena</Link>
              <Link to="/company" className="hover:text-[#B91C1C] transition-colors" data-testid="nav-link-companies">For Companies</Link>
              <Link to="/colleges" className="hover:text-[#B91C1C] transition-colors" data-testid="nav-link-colleges">For Colleges</Link>
            </nav>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <button onClick={() => navigate('/codearena/auth')} className="text-[13px] font-bold text-[#171717] hover:text-[#B91C1C] px-4 py-2 transition-colors" data-testid="nav-cta-login">
              Login
            </button>
            <button onClick={() => navigate('/company/auth')} className="text-[13px] font-bold bg-[#B91C1C] text-white px-5 py-2.5 rounded-full hover:bg-[#7F1D1D] hover:-translate-y-0.5 hover:shadow-[0_12px_24px_-8px_rgba(185,28,28,0.25)] transition-all" data-testid="nav-cta-getstarted">
              Get Started
            </button>
          </div>
          <button
            className="md:hidden text-[#171717] p-2 -mr-2 relative z-[60]"
            onClick={() => setMobileOpen(v => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Drawer — rendered as sibling of header (outside backdrop-blur containing block) */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-white overflow-y-auto z-50"
          data-testid="mobile-menu"
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
            <Logo size="md" />
            <button
              onClick={() => setMobileOpen(false)}
              className="text-[#171717] p-2 -mr-2"
              aria-label="Close menu"
              data-testid="mobile-menu-close"
            >
              <X size={24} />
            </button>
          </div>
          <div className="px-6 py-8 flex flex-col gap-1">
            {[
              { to: '/', label: 'Home', slug: 'home' },
              { to: '/codearena', label: 'EvalentX Arena', slug: 'arena' },
              { to: '/company', label: 'For Companies', slug: 'companies' },
              { to: '/colleges', label: 'For Colleges', slug: 'colleges' },
              { to: '/codearena/jobs', label: 'Jobs', slug: 'jobs' },
              { to: '/codearena/mock-interview', label: 'Mock Interviews', slug: 'mock-interviews' },
            ].map(link => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className="text-lg font-bold text-[#0A0A0A] hover:text-[#B91C1C] py-3 border-b border-[#F5F5F5]"
                data-testid={`mobile-nav-${link.slug}`}
              >
                {link.label}
              </Link>
            ))}
            <div className="mt-6 flex flex-col gap-3">
              <button
                onClick={() => { setMobileOpen(false); navigate('/codearena/auth'); }}
                className="w-full border border-[#E5E5E5] text-[#0A0A0A] py-3.5 rounded-full font-bold text-[14px] hover:border-[#B91C1C]"
                data-testid="mobile-cta-login"
              >
                Login
              </button>
              <button
                onClick={() => { setMobileOpen(false); navigate('/company/auth'); }}
                className="w-full bg-[#B91C1C] text-white py-3.5 rounded-full font-bold text-[14px]"
                data-testid="mobile-cta-getstarted"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/* ─── Dashboard Mockup (Ruby Palette) ─── */
const DashboardMockup = () => (
  <div className="relative w-full rounded-2xl overflow-hidden border border-[#E5E5E5] shadow-[0_24px_48px_-12px_rgba(0,0,0,0.08)] bg-white">
    <div className="flex items-center gap-2 px-4 py-3 border-b border-[#F5F5F5] bg-[#FAFAF9]">
      <div className="w-2.5 h-2.5 rounded-full bg-[#E5E5E5]" />
      <div className="w-2.5 h-2.5 rounded-full bg-[#E5E5E5]" />
      <div className="w-2.5 h-2.5 rounded-full bg-[#E5E5E5]" />
      <span className="ml-3 text-[10px] text-[#A3A3A3] font-bold tracking-[0.2em] uppercase">Talent Pipeline</span>
    </div>
    <div className="p-6 space-y-5">
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Candidates', value: '2,847', change: '+12%', color: 'text-[#B91C1C]' },
          { label: 'Assessed', value: '156', change: '+8%', color: 'text-[#171717]' },
          { label: 'Avg. Score', value: '88.4', change: '+3.2', color: 'text-[#171717]' },
        ].map((s, i) => (
          <div key={i} className="border border-[#F5F5F5] rounded-xl p-4 bg-white">
            <p className="text-[10px] text-[#A3A3A3] uppercase tracking-[0.2em] font-bold">{s.label}</p>
            <p className="text-2xl font-extrabold text-[#0A0A0A] mt-2 tracking-tight">{s.value}</p>
            <p className={`text-[11px] font-bold ${s.color} mt-1`}>{s.change}</p>
          </div>
        ))}
      </div>
      <div className="bg-[#FAFAF9] rounded-xl p-5 h-36 flex items-end gap-2 border border-[#F5F5F5]">
        {[40, 65, 50, 80, 70, 95, 60, 85, 75, 90, 68, 88].map((h, i) => (
          <div key={i} className="flex-1 rounded-t transition-all duration-700 hover:opacity-80" style={{ height: `${h}%`, background: i % 3 === 0 ? '#B91C1C' : i % 3 === 1 ? '#171717' : '#E5E5E5' }} />
        ))}
      </div>
      <div className="rounded-xl border border-[#F5F5F5] overflow-hidden">
        <div className="grid grid-cols-4 text-[10px] font-bold text-[#A3A3A3] uppercase tracking-[0.2em] bg-[#FAFAF9] px-4 py-3">
          <span>Candidate</span><span>Score</span><span>Match</span><span>Status</span>
        </div>
        {[
          { name: 'Priya Sharma', score: '92', match: '98%', status: 'Shortlisted' },
          { name: 'Arjun Patel', score: '87', match: '91%', status: 'In Review' },
          { name: 'Neha Gupta', score: '95', match: '99%', status: 'Hired' },
        ].map((r, i) => (
          <div key={i} className="grid grid-cols-4 text-[13px] text-[#525252] px-4 py-3 border-t border-[#F5F5F5] bg-white">
            <span className="font-bold text-[#171717]">{r.name}</span>
            <span className="font-semibold">{r.score}</span>
            <span className="text-[#A3A3A3] font-semibold">{r.match}</span>
            <span className={`text-[11px] font-bold ${r.status === 'Hired' ? 'text-[#B91C1C]' : 'text-[#171717]'}`}>{r.status}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ─── Analytics Mockup (Ruby Palette) ─── */
const AnalyticsMockup = () => (
  <div className="relative w-full rounded-2xl overflow-hidden border border-[#E5E5E5] shadow-[0_24px_48px_-12px_rgba(0,0,0,0.08)] bg-white">
    <div className="flex items-center gap-2 px-4 py-3 border-b border-[#F5F5F5] bg-[#FAFAF9]">
      <div className="w-2.5 h-2.5 rounded-full bg-[#E5E5E5]" />
      <div className="w-2.5 h-2.5 rounded-full bg-[#E5E5E5]" />
      <div className="w-2.5 h-2.5 rounded-full bg-[#E5E5E5]" />
      <span className="ml-3 text-[10px] text-[#A3A3A3] font-bold tracking-[0.2em] uppercase">Campus Analytics</span>
    </div>
    <div className="p-6 space-y-5">
      <div className="grid grid-cols-2 gap-4">
        {[
          { label: 'Active Students', value: '4,218', icon: Users },
          { label: 'Placement Rate', value: '89%', icon: TrendingUp },
        ].map((s, i) => (
          <div key={i} className="border border-[#F5F5F5] bg-white rounded-xl p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-[#FEF2F2] flex items-center justify-center">
              <s.icon className="text-[#B91C1C]" size={18} />
            </div>
            <div>
              <p className="text-[10px] text-[#A3A3A3] uppercase tracking-[0.2em] font-bold">{s.label}</p>
              <p className="text-xl font-extrabold text-[#0A0A0A] tracking-tight">{s.value}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-6 border border-[#F5F5F5] bg-[#FAFAF9] rounded-xl p-5">
        <div className="relative w-20 h-20 flex-shrink-0">
          <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
            <circle cx="18" cy="18" r="14" fill="none" stroke="#E5E5E5" strokeWidth="4" />
            <circle cx="18" cy="18" r="14" fill="none" stroke="#B91C1C" strokeWidth="4" strokeDasharray="62 88" />
            <circle cx="18" cy="18" r="14" fill="none" stroke="#171717" strokeWidth="4" strokeDasharray="18 88" strokeDashoffset="-62" />
          </svg>
        </div>
        <div className="space-y-2 text-[13px] w-full">
          {[
            { label: 'Engineering', pct: '70%', color: 'bg-[#B91C1C]' },
            { label: 'Data Science', pct: '20%', color: 'bg-[#171717]' },
          ].map((d, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className={`w-2 h-2 rounded-full ${d.color}`} />
              <span className="text-[#525252] font-semibold">{d.label}</span>
              <span className="font-extrabold text-[#0A0A0A] ml-auto">{d.pct}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

/* ─── Events Carousel Section ─── */
const EventsCarousel = ({ events, navigate }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const fallbackImages = [
    'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&q=80',
    'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=600&q=80',
    'https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=600&q=80',
  ];

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 400, behavior: 'smooth' });
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll);
      checkScroll();
      return () => el.removeEventListener('scroll', checkScroll);
    }
  }, [events]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (!events || events.length === 0) return null;

  return (
    <section className="py-24 md:py-32 bg-white border-t border-[#F5F5F5]" data-testid="events-carousel-section">
      <div className="max-w-7xl mx-auto px-6">
        <FadeInSection>
          <div className="flex items-end justify-between mb-16 border-b border-[#F5F5F5] pb-8">
            <div className="max-w-xl">
              <span className="text-[11px] font-bold text-[#A3A3A3] uppercase tracking-[0.2em] mb-4 block">Upcoming Events</span>
              <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-[#0A0A0A] leading-tight">
                Learn, compete, and <span className="text-[#B91C1C]">connect.</span>
              </h2>
            </div>
            <div className="hidden md:flex items-center gap-3">
              <button onClick={() => scroll(-1)} disabled={!canScrollLeft} className={`w-12 h-12 rounded-full border flex items-center justify-center transition-all ${canScrollLeft ? 'border-[#E5E5E5] hover:border-[#B91C1C] hover:text-[#B91C1C] text-[#171717]' : 'border-[#F5F5F5] text-[#E5E5E5] cursor-not-allowed'}`} data-testid="events-scroll-left">
                <ChevronLeft size={20} />
              </button>
              <button onClick={() => scroll(1)} disabled={!canScrollRight} className={`w-12 h-12 rounded-full border flex items-center justify-center transition-all ${canScrollRight ? 'border-[#E5E5E5] hover:border-[#B91C1C] hover:text-[#B91C1C] text-[#171717]' : 'border-[#F5F5F5] text-[#E5E5E5] cursor-not-allowed'}`} data-testid="events-scroll-right">
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </FadeInSection>

        <div ref={scrollRef} className="flex gap-8 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-10">
          {events.map((event, i) => (
            <FadeInSection key={event.id || i} delay={i * 0.05} className="snap-start flex-shrink-0 w-[340px] md:w-[400px]">
              <div className="group rounded-[20px] border border-[#E5E5E5] bg-white overflow-hidden hover:shadow-[0_24px_48px_-12px_rgba(185,28,28,0.08)] hover:border-[#B91C1C]/20 transition-all duration-500 h-full flex flex-col cursor-pointer" onClick={() => navigate(`/events/${event.id}`)}>
                <div className="relative h-56 overflow-hidden bg-[#FAFAF9]">
                  <img src={event.cover_image || fallbackImages[i % fallbackImages.length]} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" onError={(e) => { e.target.src = fallbackImages[i % fallbackImages.length]; }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <div className="absolute top-4 right-4 bg-white text-[#171717] text-[10px] font-extrabold uppercase tracking-wider px-3 py-1.5 rounded-full shadow-sm">
                    {event.is_paid ? 'Paid' : 'Free Entry'}
                  </div>
                  <div className="absolute bottom-4 left-4">
                    <span className="text-white text-[11px] font-bold bg-[#B91C1C] rounded-full px-3 py-1.5 tracking-wide">
                      {event.company_name}
                    </span>
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-grow">
                  <div className="flex items-center gap-4 text-[11px] font-bold uppercase tracking-wider text-[#A3A3A3] mb-4">
                    <span className="flex items-center gap-1.5"><Calendar size={14} />{formatDate(event.event_date)}</span>
                    {event.event_type && <span className="flex items-center gap-1.5"><MapPin size={14} />{event.event_type === 'online' ? 'Online' : event.venue || 'TBA'}</span>}
                  </div>
                  <h3 className="text-xl font-bold text-[#0A0A0A] mb-3 leading-snug group-hover:text-[#B91C1C] transition-colors">{event.title}</h3>
                  <p className="text-sm text-[#525252] leading-relaxed mb-6 line-clamp-2">{event.description || 'Join this exciting event and connect with industry professionals.'}</p>
                  <div className="mt-auto pt-4 border-t border-[#F5F5F5] flex items-center justify-between">
                    <span className="text-[13px] font-bold text-[#171717]">View Details</span>
                    <ArrowRight size={16} className="text-[#A3A3A3] group-hover:text-[#B91C1C] group-hover:translate-x-1 transition-all" />
                  </div>
                </div>
              </div>
            </FadeInSection>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Verified Talent Showcase Strip ─── */
const VerifiedTalentShowcase = ({ profiles, navigate }) => {
  if (!profiles || profiles.length === 0) return null;

  const avatarColors = [
    'bg-[#B91C1C]', 'bg-[#0A0A0A]', 'bg-[#7F1D1D]', 'bg-[#171717]',
    'bg-[#9A3412]', 'bg-[#1E3A8A]', 'bg-[#065F46]', 'bg-[#5B21B6]'
  ];

  // Duplicate for a seamless loop when there are few profiles
  const loopProfiles = profiles.length < 6 ? [...profiles, ...profiles] : profiles;

  const Card = ({ p, idx }) => (
    <div
      onClick={() => p.profile_url && navigate(p.profile_url)}
      onKeyDown={(e) => { if (p.profile_url && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); navigate(p.profile_url); } }}
      role={p.profile_url ? 'button' : undefined}
      tabIndex={p.profile_url ? 0 : undefined}
      aria-label={p.profile_url ? `View ${p.name}'s profile` : undefined}
      className={`group w-[300px] shrink-0 mx-3 rounded-[20px] border border-[#E5E5E5] bg-white p-6 transition-all duration-500 hover:border-[#B91C1C]/40 hover:shadow-[0_24px_48px_-12px_rgba(185,28,28,0.10)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B91C1C] ${p.profile_url ? 'cursor-pointer' : ''}`}
      data-testid={`verified-showcase-card-${idx}`}
    >
      <div className="flex items-center gap-4 mb-5">
        {p.avatar_url ? (
          <img
            src={p.avatar_url.startsWith('http') ? p.avatar_url : `${BACKEND_URL}${p.avatar_url}`}
            alt={p.name}
            onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling.style.display = 'flex'; }}
            className="w-12 h-12 rounded-full object-cover shrink-0"
          />
        ) : null}
        <div
          className={`w-12 h-12 rounded-full ${avatarColors[idx % avatarColors.length]} items-center justify-center text-white font-extrabold text-[15px] shrink-0`}
          style={{ display: p.avatar_url ? 'none' : 'flex' }}
        >
          {p.initials}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="font-extrabold text-[#0A0A0A] text-[15px] truncate">{p.name}</p>
            <Shield size={14} className="text-[#B91C1C] shrink-0" fill="currentColor" />
          </div>
          {p.college && <p className="text-[13px] text-[#737373] truncate">{p.college}</p>}
        </div>
      </div>
      <div className="flex items-center justify-between mb-4">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#B91C1C] bg-[#FEF2F2] px-2.5 py-1 rounded-full">
          <Award size={13} /> {p.plan_name}
        </span>
        {p.national_rank ? (
          <span className="inline-flex items-center gap-1 text-[12px] font-bold text-[#171717]">
            <TrendingUp size={13} className="text-[#B91C1C]" /> Rank #{p.national_rank}
          </span>
        ) : (
          <span className="text-[13px] font-bold text-[#171717]">Score {p.best_score}</span>
        )}
      </div>
      {p.skills && p.skills.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {p.skills.map((s, i) => (
            <span key={i} className="text-[11px] font-medium text-[#525252] bg-[#FAFAF9] border border-[#F0F0F0] px-2 py-0.5 rounded-md">{s}</span>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <section className="py-20 md:py-24 bg-[#FAFAF9] border-t border-[#E5E5E5]" data-testid="verified-showcase-section">
      <div className="max-w-7xl mx-auto px-6 mb-12">
        <FadeInSection>
          <span className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.15em] text-[#B91C1C] mb-4">
            <Shield size={14} fill="currentColor" /> Verified Talent
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0A0A0A] max-w-2xl leading-tight">
            Meet developers verified by EvalentX
          </h2>
          <p className="text-lg text-[#525252] font-medium mt-3 max-w-xl">
            Real profiles, real performance data. These developers earned their verified badge.
          </p>
        </FadeInSection>
      </div>
      <div className="relative">
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-r from-[#FAFAF9] to-transparent z-10" />
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-l from-[#FAFAF9] to-transparent z-10" />
        <Marquee gradient={false} speed={40} pauseOnHover={true}>
          {loopProfiles.map((p, i) => (
            <Card key={`${p.student_id}-${i}`} p={p} idx={i} />
          ))}
        </Marquee>
      </div>
      <div className="max-w-7xl mx-auto px-6 mt-12 flex justify-center">
        <button
          onClick={() => navigate('/verified-developers')}
          className="group inline-flex items-center gap-3 bg-[#171717] text-white px-8 py-4 rounded-full font-bold text-[15px] hover:bg-[#0A0A0A] hover:-translate-y-0.5 hover:shadow-[0_12px_24px_-8px_rgba(0,0,0,0.3)] transition-all"
          data-testid="browse-verified-developers-btn"
        >
          Browse all verified developers
          <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </section>
  );
};

/* ─── Main Component ─── */
const HomeNew = () => {
  const navigate = useNavigate();
  const [notificationBanner, setNotificationBanner] = useState(null);
  const [showBanner, setShowBanner] = useState(true);
  const [events, setEvents] = useState([]);
  const [verifiedProfiles, setVerifiedProfiles] = useState([]);

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const response = await axios.get(`${API}/settings/notification-banner`);
        setNotificationBanner(response.data);
        setShowBanner(response.data.is_active);
      } catch (err) {
        setNotificationBanner(null);
        setShowBanner(false);
      }
    };
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/public/events?page=1&per_page=6`);
        const data = response.data;
        setEvents(data.events || []);
      } catch (err) {
        setEvents([]);
      }
    };
    const fetchVerifiedProfiles = async () => {
      try {
        const response = await axios.get(`${API}/public/verified-talent/showcase?limit=12`);
        setVerifiedProfiles(response.data.profiles || []);
      } catch (err) {
        setVerifiedProfiles([]);
      }
    };
    fetchBanner();
    fetchEvents();
    fetchVerifiedProfiles();
  }, []);

  const trustedLogos = [
    'IIT Gandhinagar', 'IIT Bombay', 'BITS Pilani', 'NIT Trichy',
    'IIIT Hyderabad', 'VIT Vellore', 'SRM University', 'Manipal University',
    'Jadavpur University', 'DTU Delhi', 'NSUT Delhi', 'PES University'
  ];

  const hiringPartners = [
    'Amazon', 'Flipkart', 'Razorpay', 'Zomato', 'Swiggy', 'CRED',
    'PhonePe', 'Paytm', 'Meesho', 'Zerodha', 'Groww', 'Nykaa',
    'Freshworks', 'Postman', 'Rippling', 'Uber', 'Oyo', 'BYJU\'S'
  ];

  const testimonials = [
    {
      name: 'Priya Sharma',
      role: 'SDE I · Razorpay',
      college: 'NIT Trichy, 2025',
      photo: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=256&h=256&fit=crop&crop=face',
      outcome: '₹24 LPA',
      quote: 'EvalentX Arena contests built the muscle I needed for real interviews. The verified Skill Passport got me shortlisted by Razorpay in 48 hours.',
    },
    {
      name: 'Arjun Mehta',
      role: 'ML Engineer · Flipkart',
      college: 'IIIT Hyderabad, 2024',
      photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=256&h=256&fit=crop&crop=face',
      outcome: '₹32 LPA',
      quote: 'The AI mock interviews with Athena were unreal. By the time I walked into Flipkart’s onsite, I had already simulated 20+ rounds of system design.',
    },
    {
      name: 'Neha Gupta',
      role: 'Backend Engineer · CRED',
      college: 'BITS Pilani, 2025',
      photo: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=256&h=256&fit=crop&crop=face',
      outcome: '₹28 LPA',
      quote: 'I went from applying blindly on job portals to companies reaching out directly. The verified profile changed everything about my hiring experience.',
    },
    {
      name: 'Rohan Das',
      role: 'Founding Engineer · PhonePe',
      college: 'IIT Bombay, 2024',
      photo: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=256&h=256&fit=crop&crop=face',
      outcome: '₹40 LPA',
      quote: 'National contests here aren’t toy problems — they mirror what top teams actually build. Winning one landed me 3 founding-team offers.',
    },
  ];

  const recruiterQuote = {
    name: 'Kavita Nair',
    role: 'VP Engineering · Groww',
    photo: 'https://images.unsplash.com/photo-1567532939604-b6b5b0db2604?w=256&h=256&fit=crop&crop=face',
    quote: 'EvalentX cut our early-funnel screening time by 70%. Every candidate we source here already has verified signal — we move straight to onsite.',
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] text-[#171717] font-sans selection:bg-[#B91C1C] selection:text-white" data-testid="homepage">
      
      {/* ─── Notification Banner ─── */}
      {showBanner && notificationBanner && notificationBanner.is_active && (
        <div className="relative z-50 bg-[#B91C1C] text-white py-3 px-6" data-testid="notification-banner">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
            <AlertCircle size={16} className="flex-shrink-0 opacity-80" />
            <p className="text-xs md:text-sm font-bold tracking-wide text-center pr-6">
              {notificationBanner.text}
            </p>
            <button
              onClick={() => setShowBanner(false)}
              className="absolute right-4 md:right-8 text-white/70 hover:text-white transition-colors"
              data-testid="close-notification-banner"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <TopNav />

      {/* ─── HERO SECTION (Full Viewport, Grand) ─── */}
      <section className="relative overflow-hidden bg-white pt-12 pb-16 sm:pt-16 sm:pb-24 md:pt-24 md:pb-36 min-h-[calc(100vh-3.5rem)] flex items-center border-b border-[#E5E5E5]" data-testid="hero-section">
        {/* Subtle Noise / Watermark */}
        <div className="absolute top-10 -right-20 opacity-[0.02] pointer-events-none transform rotate-[-10deg] hidden md:block">
          <span className="font-extrabold text-[160px] lg:text-[240px] tracking-tighter leading-none whitespace-nowrap text-[#B91C1C]">
            EVALENTX
          </span>
        </div>

        <div className="relative max-w-[1280px] mx-auto px-5 sm:px-6 md:px-8 w-full">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 relative z-10">
              <FadeInSection>
                <div className="inline-flex items-center gap-3 mb-10 border border-[#E5E5E5] bg-white rounded-full pl-2 pr-5 py-1.5 shadow-sm">
                  <span className="flex h-2 w-2 relative ml-1">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#B91C1C] opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#B91C1C]"></span>
                  </span>
                  <span className="text-[11px] font-bold text-[#A3A3A3] uppercase tracking-[0.2em]">Platform Live — 50,000+ Users</span>
                </div>
              </FadeInSection>

              <FadeInSection delay={0.1}>
                <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[7rem] font-extrabold tracking-tighter text-[#0A0A0A] leading-[0.95] mb-10 break-words" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
                  Compete. <br />
                  Evaluate. <br />
                  <span className="relative inline-block text-[#B91C1C]">
                    Get Hired.
                    <span className="absolute -bottom-1 left-0 w-full h-2 bg-[#B91C1C]/25 transform -skew-x-12"></span>
                  </span>
                </h1>
              </FadeInSection>

              <FadeInSection delay={0.2}>
                <p className="text-xl md:text-2xl text-[#525252] leading-relaxed mb-12 max-w-xl font-medium">
                  Compete in national-grade challenges, prove your skills with verified assessments,
                  and get hired by elite companies. The modern standard for tech recruitment.
                </p>
              </FadeInSection>

              <FadeInSection delay={0.3}>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => navigate('/company/auth')}
                    className="group flex items-center justify-center gap-3 bg-[#B91C1C] text-white px-9 py-5 rounded-full font-bold text-[15px] hover:bg-[#7F1D1D] hover:-translate-y-0.5 hover:shadow-[0_24px_48px_-12px_rgba(185,28,28,0.35)] transition-all duration-300"
                    data-testid="hero-cta-companies"
                  >
                    <Building2 size={18} />
                    Hire Top Talent
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button
                    onClick={() => navigate('/codearena')}
                    className="group flex items-center justify-center gap-3 bg-white text-[#171717] border-[1.5px] border-[#E5E5E5] px-9 py-5 rounded-full font-bold text-[15px] hover:border-[#B91C1C] hover:bg-[#FEF2F2] hover:text-[#B91C1C] hover:-translate-y-0.5 transition-all duration-300"
                    data-testid="hero-cta-students"
                  >
                    <Code size={18} />
                    Join EvalentX Arena
                  </button>
                </div>
              </FadeInSection>
            </div>

            {/* Right Graphic */}
            <div className="lg:col-span-5 relative hidden lg:block">
              <FadeInSection delay={0.4}>
                <div className="relative">
                  <div className="absolute -inset-6 bg-[#B91C1C]/5 rounded-[32px] blur-2xl transform rotate-3" />
                  <DashboardMockup />
                </div>
              </FadeInSection>
            </div>
          </div>
        </div>
      </section>

      {/* ─── STATS STRIP — NUMBERS THAT MATTER (Grand) ─── */}
      <section className="bg-white border-b border-[#E5E5E5]" data-testid="stats-strip">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8 pt-24 pb-8">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <p className="text-[11px] font-bold text-[#A3A3A3] uppercase tracking-[0.25em] mb-3">Numbers that matter</p>
              <h2 className="text-4xl md:text-5xl font-extrabold tracking-tighter text-[#0A0A0A]" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
                Built at scale, trusted by talent.
              </h2>
            </div>
            <p className="text-sm text-[#525252] max-w-sm font-medium">
              Real numbers from a real platform — every metric updated live as developers compete and companies hire.
            </p>
          </div>
        </div>
        <div className="max-w-[1280px] mx-auto overflow-hidden">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-[#E5E5E5] border-t border-[#E5E5E5]">
            {[
              { value: 50000, suffix: '+', label: 'Verified Developers', accent: false },
              { value: 500, suffix: '+', label: 'Companies Hiring', accent: false },
              { value: 200, suffix: '+', label: 'Partner Colleges', accent: false },
              { value: 10000, suffix: '+', label: 'Successful Hires', accent: true },
            ].map((stat, i) => (
              <div key={i} className="min-w-0 overflow-hidden py-12 md:py-16 lg:py-20 px-4 sm:px-5 lg:px-6 xl:px-8 flex flex-col items-start group hover:bg-[#FAFAF9] transition-colors">
                <span className={`block w-full whitespace-nowrap text-[clamp(2rem,9vw,4rem)] lg:text-[clamp(2.25rem,4.4vw,4.5rem)] font-extrabold tracking-tighter mb-3 leading-none ${stat.accent ? 'text-[#B91C1C]' : 'text-[#0A0A0A] group-hover:text-[#B91C1C]'} transition-colors`} style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif", fontVariantNumeric: 'tabular-nums' }}>
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                </span>
                <span className="text-[10px] sm:text-[11px] font-bold text-[#A3A3A3] uppercase tracking-[0.2em] truncate w-full">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── TRUSTED BY ─── */}
      <section className="py-16 bg-[#FAFAF9] border-b border-[#E5E5E5]" data-testid="trusted-by-section">
        <div className="max-w-7xl mx-auto px-6 mb-8 text-center">
          <span className="text-[10px] font-bold text-[#A3A3A3] uppercase tracking-[0.25em]">Trusted by talent from leading institutions</span>
        </div>
        <Marquee speed={40} gradient gradientColor="#FAFAF9" gradientWidth={150} pauseOnHover>
          {trustedLogos.map((logo, i) => (
            <div key={i} className="mx-12 flex items-center gap-3 opacity-40 hover:opacity-100 transition-opacity cursor-default">
              <GraduationCap size={20} className="text-[#171717]" />
              <span className="text-sm font-extrabold tracking-wide text-[#171717]">{logo}</span>
            </div>
          ))}
        </Marquee>
      </section>

      {/* ─── HIRING PARTNERS (Companies) ─── */}
      <section className="py-16 bg-white border-b border-[#E5E5E5]" data-testid="hiring-partners-section">
        <div className="max-w-7xl mx-auto px-6 mb-8 text-center">
          <span className="text-[10px] font-bold text-[#B91C1C] uppercase tracking-[0.25em]">Where our talent gets hired</span>
        </div>
        <Marquee speed={35} direction="right" gradient gradientColor="#FFFFFF" gradientWidth={150} pauseOnHover>
          {hiringPartners.map((company, i) => (
            <div key={i} className="mx-10 flex items-center gap-3 opacity-50 hover:opacity-100 transition-opacity cursor-default">
              <Building2 size={18} className="text-[#B91C1C]" />
              <span className="text-sm font-extrabold tracking-wide text-[#0A0A0A]">{company}</span>
            </div>
          ))}
        </Marquee>
      </section>

      {/* ─── KEY OFFERINGS (4 PILLARS) ─── */}
      <section className="py-24 md:py-32 bg-white" data-testid="key-offerings-section">
        <div className="max-w-7xl mx-auto px-6">
          <FadeInSection>
            <div className="mb-16 md:mb-24 flex flex-col md:flex-row md:items-end justify-between gap-8 border-b border-[#E5E5E5] pb-8">
              <div className="max-w-2xl">
                <span className="text-[11px] font-bold text-[#A3A3A3] uppercase tracking-[0.2em] mb-4 block">Platform Pillars</span>
                <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-[#0A0A0A] leading-tight">
                  Everything you need to <span className="text-[#B91C1C]">evaluate</span> and scale.
                </h2>
              </div>
              <p className="text-lg text-[#525252] font-medium max-w-sm md:text-right">
                A unified ecosystem connecting verified talent with global opportunities.
              </p>
            </div>
          </FadeInSection>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                title: 'Jobs & Internships',
                desc: 'Access premium roles. Get discovered by top companies based on your verified skills.',
                icon: Briefcase,
                path: '/codearena/jobs',
                color: 'bg-white'
              },
              {
                title: 'AI Mock Interviews',
                desc: 'Practice with Athena AI. Get deep analytics on your communication and technical knowledge.',
                icon: Brain,
                path: '/codearena/mock-interview',
                color: 'bg-[#FAFAF9]'
              },
              {
                title: 'Coding Challenges',
                desc: 'Compete in national hackathons and algorithmic contests. Climb the leaderboard.',
                icon: Trophy,
                path: '/codearena/contests',
                color: 'bg-[#FAFAF9]'
              },
              {
                title: 'Verified Profiles',
                desc: 'Build a resume backed by real performance data, XP, and verified certificates.',
                icon: Shield,
                path: '/verified-talent',
                color: 'bg-white'
              }
            ].map((pillar, i) => (
              <FadeInSection key={i} delay={i * 0.1}>
                <div 
                  onClick={() => navigate(pillar.path)}
                  className={`group relative p-10 ${pillar.color} rounded-[24px] border border-[#E5E5E5] hover:border-[#B91C1C] hover:shadow-[0_24px_48px_-12px_rgba(185,28,28,0.08)] transition-all duration-500 cursor-pointer h-full flex flex-col`}
                  data-testid={`offering-card-${i}`}
                >
                  <div className="w-14 h-14 rounded-full bg-white border border-[#E5E5E5] flex items-center justify-center mb-8 group-hover:scale-110 group-hover:border-[#B91C1C] transition-all">
                    <pillar.icon size={24} className="text-[#171717] group-hover:text-[#B91C1C]" />
                  </div>
                  <h3 className="text-2xl font-extrabold text-[#0A0A0A] mb-4">{pillar.title}</h3>
                  <p className="text-[#525252] leading-relaxed mb-8 flex-grow">{pillar.desc}</p>
                  <div className="flex items-center gap-2 text-[13px] font-bold text-[#171717] group-hover:text-[#B91C1C] mt-auto">
                    Explore <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── VERIFIED TALENT SHOWCASE ─── */}
      <VerifiedTalentShowcase profiles={verifiedProfiles} navigate={navigate} />

      {/* ─── FOR COMPANIES ─── */}
      <section className="py-24 md:py-32 bg-[#FAFAF9] border-t border-[#E5E5E5]" data-testid="companies-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <FadeInSection>
                <span className="text-[11px] font-bold text-[#B91C1C] uppercase tracking-[0.2em] mb-4 block">For Companies</span>
                <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-[#0A0A0A] leading-tight mb-6">
                  Hire talent that's already proven.
                </h2>
                <p className="text-lg text-[#525252] leading-relaxed mb-10">
                  Skip the guesswork. Access skill-verified developers, run AI-powered assessments, and host branded hiring challenges — all from one premium dashboard.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 mb-10 text-[14px] font-bold text-[#171717]">
                  {['AI-Powered Talent Search', 'Custom Assessments', 'Branded Challenges', 'Real-Time Analytics'].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#B91C1C]" />
                      {item}
                    </div>
                  ))}
                </div>
                <button onClick={() => navigate('/company/auth')} className="group flex items-center justify-center gap-3 bg-[#171717] text-white px-8 py-4 rounded-full font-bold text-[15px] hover:bg-[#0A0A0A] hover:-translate-y-0.5 hover:shadow-[0_12px_24px_-8px_rgba(0,0,0,0.3)] transition-all">
                  Start Hiring Free <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </FadeInSection>
            </div>
            <div className="relative hidden lg:block">
              <FadeInSection delay={0.2}>
                <div className="absolute -inset-4 bg-[#B91C1C]/5 rounded-[32px] blur-2xl transform -rotate-3" />
                <DashboardMockup />
              </FadeInSection>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FOR COLLEGES ─── */}
      <section className="py-24 md:py-32 bg-white border-t border-[#E5E5E5]" data-testid="colleges-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 relative hidden lg:block">
              <FadeInSection delay={0.2}>
                <div className="absolute -inset-4 bg-[#171717]/5 rounded-[32px] blur-2xl transform rotate-2" />
                <AnalyticsMockup />
              </FadeInSection>
            </div>
            <div className="order-1 lg:order-2">
              <FadeInSection>
                <span className="text-[11px] font-bold text-[#A3A3A3] uppercase tracking-[0.2em] mb-4 block">For Colleges</span>
                <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-[#0A0A0A] leading-tight mb-6">
                  Transform placement outcomes.
                </h2>
                <p className="text-lg text-[#525252] leading-relaxed mb-10">
                  Empower your students with national-level competitions, real-time skill analytics, and direct company connections. Elevate your institution's metrics.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 mb-10 text-[14px] font-bold text-[#171717]">
                  {['Host Campus Hackathons', 'Student Analytics', 'Placement Readiness', 'Company Networks'].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#171717]" />
                      {item}
                    </div>
                  ))}
                </div>
                <button onClick={() => navigate('/colleges')} className="group flex items-center justify-center gap-3 bg-white text-[#171717] border-[1.5px] border-[#E5E5E5] px-8 py-4 rounded-full font-bold text-[15px] hover:border-[#171717] hover:bg-[#FAFAF9] hover:-translate-y-0.5 transition-all">
                  Partner With Us <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </FadeInSection>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FOR DEVELOPERS (Persona Section) ─── */}
      <section className="py-24 md:py-32 bg-[#FAFAF9] border-t border-[#E5E5E5]" data-testid="developers-section">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <FadeInSection>
            <span className="text-[11px] font-bold text-[#B91C1C] uppercase tracking-[0.2em] mb-4 block">For Developers</span>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-[#0A0A0A] leading-tight mb-6">
              Your career starts with real challenges.
            </h2>
            <p className="text-lg text-[#525252] leading-relaxed mb-10 max-w-2xl mx-auto">
              Build a verified developer profile, compete in national-level competitions, and get discovered by top companies — all through EvalentX Arena.
            </p>
            <button onClick={() => navigate('/codearena')} className="group inline-flex items-center justify-center gap-3 bg-[#B91C1C] text-white px-8 py-4 rounded-full font-bold text-[15px] hover:bg-[#7F1D1D] hover:-translate-y-0.5 hover:shadow-[0_24px_48px_-12px_rgba(185,28,28,0.25)] transition-all">
              <Code size={18} /> Start Building <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </FadeInSection>
        </div>
      </section>

      {/* ─── EVENTS CAROUSEL ─── */}
      <EventsCarousel events={events} navigate={navigate} />

      {/* ─── TESTIMONIALS & OUTCOMES ─── */}
      <section className="py-24 md:py-32 bg-white border-t border-[#E5E5E5]" data-testid="testimonials-section">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <FadeInSection>
            <div className="grid lg:grid-cols-12 gap-12 mb-16 md:mb-20 border-b border-[#F5F5F5] pb-10">
              <div className="lg:col-span-6">
                <span className="text-[11px] font-bold text-[#A3A3A3] uppercase tracking-[0.25em] mb-4 block">Real outcomes, real people</span>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter text-[#0A0A0A] leading-[0.98]" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
                  Careers built <br />
                  <span className="text-[#B91C1C]">on proof,</span> not résumés.
                </h2>
              </div>
              <div className="lg:col-span-6 flex items-end">
                <p className="text-lg md:text-xl text-[#525252] font-medium leading-relaxed">
                  These are students who solved real problems, earned verified credentials, and got hired by top Indian startups & global tech firms through EvalentX.
                </p>
              </div>
            </div>
          </FadeInSection>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-10">
            {testimonials.map((t, i) => (
              <FadeInSection key={t.name} delay={i * 0.08}>
                <figure
                  className="group h-full flex flex-col bg-white border border-[#E5E5E5] rounded-[24px] p-8 md:p-10 hover:border-[#B91C1C] hover:shadow-[0_24px_48px_-12px_rgba(185,28,28,0.1)] hover:-translate-y-1 transition-all duration-500"
                  data-testid={`testimonial-${i}`}
                >
                  <Quote size={28} className="text-[#B91C1C] opacity-70 mb-6" />
                  <blockquote className="text-[17px] md:text-lg text-[#171717] leading-relaxed font-medium mb-8 flex-grow">
                    “{t.quote}”
                  </blockquote>
                  <figcaption className="flex items-center justify-between gap-4 pt-6 border-t border-[#F5F5F5]">
                    <div className="flex items-center gap-4 min-w-0">
                      <img
                        src={t.photo}
                        alt={t.name}
                        className="w-12 h-12 rounded-full object-cover border-2 border-[#F5F5F5] group-hover:border-[#B91C1C]/30 transition-colors flex-shrink-0"
                        loading="lazy"
                      />
                      <div className="min-w-0">
                        <div className="font-extrabold text-[#0A0A0A] text-[15px] truncate">{t.name}</div>
                        <div className="text-[12px] text-[#525252] font-semibold truncate">{t.role}</div>
                        <div className="text-[11px] text-[#A3A3A3] font-medium mt-0.5 truncate">{t.college}</div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-[10px] font-bold text-[#A3A3A3] uppercase tracking-[0.2em]">Placed at</div>
                      <div className="text-xl font-extrabold text-[#B91C1C] tracking-tight">{t.outcome}</div>
                    </div>
                  </figcaption>
                </figure>
              </FadeInSection>
            ))}
          </div>

          {/* Recruiter quote */}
          <FadeInSection delay={0.1}>
            <div
              className="relative overflow-hidden rounded-[28px] bg-[#0A0A0A] text-white p-10 md:p-14"
              data-testid="recruiter-testimonial"
            >
              <div className="absolute -top-12 -right-12 w-64 h-64 bg-[#B91C1C]/30 rounded-full blur-[100px]" />
              <div className="relative grid md:grid-cols-12 gap-8 items-center">
                <div className="md:col-span-2 flex md:justify-center">
                  <img
                    src={recruiterQuote.photo}
                    alt={recruiterQuote.name}
                    className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-2 border-white/20"
                    loading="lazy"
                  />
                </div>
                <div className="md:col-span-10">
                  <span className="text-[11px] font-bold text-[#B91C1C] uppercase tracking-[0.25em] mb-4 block">From the hiring side</span>
                  <p className="text-2xl md:text-[28px] font-bold leading-[1.35] tracking-tight mb-6">
                    “{recruiterQuote.quote}”
                  </p>
                  <div className="text-sm text-[#A3A3A3] font-medium">
                    <span className="text-white font-extrabold">{recruiterQuote.name}</span> — {recruiterQuote.role}
                  </div>
                </div>
              </div>
            </div>
          </FadeInSection>

          {/* Outcome mini-stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-12">
            {[
              { value: '85%', label: 'Placement rate' },
              { value: '₹18L', label: 'Median CTC' },
              { value: '48h', label: 'Median time-to-shortlist' },
              { value: '70%', label: 'Faster screening' },
            ].map((s, i) => (
              <FadeInSection key={s.label} delay={i * 0.05}>
                <div className="bg-[#FAFAF9] border border-[#E5E5E5] rounded-2xl p-6 md:p-7 hover:border-[#B91C1C]/30 transition-colors">
                  <div className="text-3xl md:text-4xl font-extrabold tracking-tight text-[#0A0A0A] mb-2" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
                    {s.value}
                  </div>
                  <div className="text-[11px] font-bold text-[#A3A3A3] uppercase tracking-[0.2em]">{s.label}</div>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─── */}
      <section className="py-32 bg-[#0A0A0A] text-white relative overflow-hidden" data-testid="final-cta-section">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#B91C1C]/10 rounded-full blur-[100px]" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <FadeInSection>
            <h2 className="text-5xl md:text-6xl font-extrabold tracking-tighter leading-tight mb-8">
              Ready to elevate your standards?
            </h2>
            <p className="text-xl text-[#A3A3A3] mb-12 font-medium">
              Join thousands of developers, companies, and colleges already using EvalentX to build the future of tech.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={() => navigate('/company/auth')} className="w-full sm:w-auto bg-[#B91C1C] hover:bg-[#7F1D1D] text-white px-8 py-4 rounded-full font-bold text-[15px] hover:-translate-y-0.5 transition-all" data-testid="final-cta-hire">
                I'm Hiring
              </button>
              <button onClick={() => navigate('/codearena')} className="w-full sm:w-auto bg-transparent border border-white/20 hover:border-white hover:bg-white/5 text-white px-8 py-4 rounded-full font-bold text-[15px] hover:-translate-y-0.5 transition-all" data-testid="final-cta-dev">
                I'm a Developer
              </button>
              <button onClick={() => navigate('/colleges')} className="w-full sm:w-auto bg-transparent border border-white/20 hover:border-white hover:bg-white/5 text-white px-8 py-4 rounded-full font-bold text-[15px] hover:-translate-y-0.5 transition-all" data-testid="final-cta-college">
                I'm a College
              </button>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ─── HOW EVALENTX WORKS — 3-step grand process ─── */}
      <section className="bg-white border-t border-[#E5E5E5]" data-testid="how-it-works">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8 py-24 md:py-36">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 mb-16">
            <div className="lg:col-span-5">
              <p className="text-[11px] font-bold text-[#A3A3A3] uppercase tracking-[0.25em] mb-4">How EvalentX works</p>
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter text-[#0A0A0A] leading-[0.95]" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
                Three steps. <br />
                <span className="text-[#B91C1C]">One platform.</span>
              </h2>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-lg md:text-xl text-[#525252] font-medium leading-relaxed">
                Whether you're a developer building a Skill Passport, a company hiring at scale,
                or a college shaping placements — EvalentX is the single rail that connects you all.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                num: '01',
                role: 'For Developers',
                title: 'Compete & build a Skill Passport',
                desc: 'Solve daily problems, dominate national contests, and earn a verified profile companies trust.',
                cta: 'Join Arena',
                to: '/codearena',
                testid: 'work-step-developers',
              },
              {
                num: '02',
                role: 'For Companies',
                title: 'Hire pre-vetted, AI-ranked talent',
                desc: 'Filter by verified scores, run AI-proctored assessments, and close the best candidates faster.',
                cta: 'Start Hiring',
                to: '/company',
                testid: 'work-step-companies',
              },
              {
                num: '03',
                role: 'For Colleges',
                title: 'Build placement-ready cohorts',
                desc: 'Track student readiness, host private contests, and connect your campus to elite recruiters.',
                cta: 'Partner With Us',
                to: '/colleges',
                testid: 'work-step-colleges',
              },
            ].map((step) => (
              <Link
                key={step.num}
                to={step.to}
                className="group relative bg-white border border-[#E5E5E5] rounded-2xl p-8 md:p-10 hover:border-[#B91C1C] hover:shadow-[0_24px_48px_-12px_rgba(185,28,28,0.18)] hover:-translate-y-1 transition-all duration-500"
                data-testid={step.testid}
              >
                <span className="block text-[5rem] md:text-[6rem] font-extrabold tracking-tighter leading-none text-[#FAFAF9] group-hover:text-[#FEF2F2] transition-colors mb-2" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
                  {step.num}
                </span>
                <p className="text-[11px] font-bold text-[#B91C1C] uppercase tracking-[0.2em] mb-3">{step.role}</p>
                <h3 className="text-2xl md:text-3xl font-extrabold tracking-tight text-[#0A0A0A] mb-4 leading-tight" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
                  {step.title}
                </h3>
                <p className="text-[#525252] text-[15px] leading-relaxed mb-8 font-medium">
                  {step.desc}
                </p>
                <span className="inline-flex items-center gap-2 text-sm font-bold text-[#0A0A0A] group-hover:text-[#B91C1C] transition-colors">
                  {step.cta}
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <GrandFooter />

    </div>
  );
};

export default HomeNew;
